// systems/bloomhold.js
// BloomHold (seed vault) deposit/withdraw logic

import { gameState } from '../game/gameState.js';
import { BLOOMHOLD_CONFIG } from '../game/constants.js';
import { getItemCategory } from '../data/items/index.js';
import logger from '../utils/logger.js';

/**
 * Check if an item is eligible for BloomHold storage
 * @param {string} itemId - Item identifier
 * @returns {boolean}
 */
export function isBloomholdEligible(itemId) {
    const category = getItemCategory(itemId);
    return BLOOMHOLD_CONFIG.ACCEPTED_CATEGORIES.includes(category);
}

/**
 * Deposit item from inventory to BloomHold
 * @param {string} itemId - Item to deposit
 * @param {number} quantity - Amount (null = all)
 * @returns {{ success: boolean, deposited: number, reason?: string }}
 */
export function depositToBloomhold(itemId, quantity = null) {
    if (!gameState.isBloomholdUnlocked()) {
        return { success: false, deposited: 0, reason: 'BloomHold not unlocked' };
    }

    if (!isBloomholdEligible(itemId)) {
        return { success: false, deposited: 0, reason: 'Item not eligible for BloomHold' };
    }

    const inventoryAmount = gameState.getItemCount(itemId);
    if (inventoryAmount === 0) {
        return { success: false, deposited: 0, reason: 'Item not in inventory' };
    }

    const toDeposit = quantity === null ? inventoryAmount : Math.min(quantity, inventoryAmount);
    const result = gameState.addBloomholdItem(itemId, toDeposit);

    if (!result.success) {
        return { success: false, deposited: 0, reason: result.reason };
    }

    gameState.removeItem(itemId, result.added);
    logger.debug(`[BLOOMHOLD] Deposited ${result.added}x ${itemId}`);

    return { success: true, deposited: result.added };
}

/**
 * Withdraw item from BloomHold to inventory
 * @param {string} itemId - Item to withdraw
 * @param {number} quantity - Amount (null = all)
 * @returns {{ success: boolean, withdrawn: number, reason?: string }}
 */
export function withdrawFromBloomhold(itemId, quantity = null) {
    if (!gameState.isBloomholdUnlocked()) {
        return { success: false, withdrawn: 0, reason: 'BloomHold not unlocked' };
    }

    const bloomholdAmount = gameState.getBloomholdItemCount(itemId);
    if (bloomholdAmount === 0) {
        return { success: false, withdrawn: 0, reason: 'Item not in BloomHold' };
    }

    const toWithdraw = quantity === null ? bloomholdAmount : Math.min(quantity, bloomholdAmount);
    const invResult = gameState.addItem(itemId, toWithdraw);

    if (!invResult.success) {
        return { success: false, withdrawn: 0, reason: invResult.reason };
    }

    gameState.removeBloomholdItem(itemId, invResult.added);
    logger.debug(`[BLOOMHOLD] Withdrew ${invResult.added}x ${itemId}`);

    return { success: true, withdrawn: invResult.added };
}

/**
 * Deposit all BloomHold-eligible items from inventory
 * @returns {{ success: boolean, deposited: object, failed: object, skippedLocked: number }}
 */
export function depositAllToBloomhold() {
    if (!gameState.isBloomholdUnlocked()) {
        return { success: false, deposited: {}, failed: {}, skippedLocked: 0, reason: 'BloomHold not unlocked' };
    }

    const deposited = {};
    const failed = {};
    let skippedLocked = 0;

    const inventorySlots = gameState.getInventorySlots();
    const eligibleItems = inventorySlots
        .filter(slot => {
            if (slot === null) return false;
            if (gameState.isItemLocked(slot.itemId)) {
                skippedLocked++;
                return false;
            }
            return isBloomholdEligible(slot.itemId);
        })
        .map(slot => ({ itemId: slot.itemId, quantity: slot.quantity }));

    for (const { itemId, quantity } of eligibleItems) {
        const result = depositToBloomhold(itemId, quantity);
        if (result.deposited > 0) {
            deposited[itemId] = result.deposited;
        }
        if (result.deposited < quantity) {
            failed[itemId] = quantity - result.deposited;
        }
    }

    return {
        success: Object.keys(failed).length === 0,
        deposited,
        failed,
        skippedLocked
    };
}

/**
 * Move BloomHold-eligible items from bank to BloomHold
 * @returns {{ success: boolean, moved: object, failed: object }}
 */
export function moveBankToBloomhold() {
    if (!gameState.isBloomholdUnlocked()) {
        return { success: false, moved: {}, failed: {}, reason: 'BloomHold not unlocked' };
    }

    const moved = {};
    const failed = {};

    // Get all bank slots that are BloomHold-eligible
    const bankSlots = gameState.getBankSlots();
    const eligibleItems = bankSlots
        .filter(slot => slot && isBloomholdEligible(slot.itemId))
        .map(slot => ({ itemId: slot.itemId, quantity: slot.quantity }));

    for (const { itemId, quantity } of eligibleItems) {
        // Try to add to BloomHold
        const result = gameState.addBloomholdItem(itemId, quantity);

        if (result.added > 0) {
            // Remove from bank what was successfully added
            gameState.removeBankItem(itemId, result.added);
            moved[itemId] = result.added;
            logger.debug(`[BLOOMHOLD] Moved ${result.added}x ${itemId} from bank`);
        }

        if (result.added < quantity) {
            failed[itemId] = quantity - result.added;
        }
    }

    return {
        success: Object.keys(failed).length === 0,
        moved,
        failed
    };
}

/**
 * Get BloomHold stats
 * @returns {{ usedSlots: number, maxSlots: number, totalItems: number, unlocked: boolean, bonusSlots: number }}
 */
export function getBloomholdStats() {
    const slots = gameState.getBloomholdSlots();
    const maxSlots = gameState.getBloomholdMaxSlots();
    const bonusSlots = gameState.getBloomholdStorageBonus();
    return {
        usedSlots: slots.length,
        maxSlots,
        totalItems: slots.reduce((sum, slot) => sum + slot.quantity, 0),
        unlocked: gameState.isBloomholdUnlocked(),
        bonusSlots
    };
}

/**
 * Get BloomHold contents with optional filtering
 * @param {string} sortBy - 'name' | 'quantity' | 'category'
 * @param {string} filter - Text filter
 * @returns {Array<{ itemId: string, quantity: number }>}
 */
export function getBloomholdContents(sortBy = 'name', filter = '') {
    let contents = [...gameState.getBloomholdSlots()];

    if (filter) {
        const lowerFilter = filter.toLowerCase();
        contents = contents.filter(item =>
            item.itemId.toLowerCase().includes(lowerFilter)
        );
    }

    switch (sortBy) {
        case 'quantity':
            contents.sort((a, b) => b.quantity - a.quantity);
            break;
        case 'category':
            contents.sort((a, b) =>
                getItemCategory(a.itemId).localeCompare(getItemCategory(b.itemId))
            );
            break;
        case 'name':
        default:
            contents.sort((a, b) => a.itemId.localeCompare(b.itemId));
    }

    return contents;
}

/**
 * Count how many BloomHold-eligible items are in the bank
 * @returns {{ count: number, quantity: number }}
 */
export function countBankEligibleForBloomhold() {
    const bankSlots = gameState.getBankSlots();
    let count = 0;
    let quantity = 0;

    for (const slot of bankSlots) {
        if (slot && isBloomholdEligible(slot.itemId)) {
            count++;
            quantity += slot.quantity;
        }
    }

    return { count, quantity };
}

export default {
    isBloomholdEligible,
    depositToBloomhold,
    withdrawFromBloomhold,
    depositAllToBloomhold,
    moveBankToBloomhold,
    getBloomholdStats,
    getBloomholdContents,
    countBankEligibleForBloomhold
};
